// src/config/featureFlags.js
//
// Central place for client feature flags (CRA inlines REACT_APP_* at build).
// Keep flags additive and default-OFF so a missing env var never turns an
// in-progress feature on in production.

// Protocol PDF → SOA extraction → source docs pipeline (Phase 9). Gates the new
// SourceX upload/review UI. The existing template flow is unaffected either way.
export const PROTOCOL_EXTRACTION_ENABLED =
  process.env.REACT_APP_ENABLE_PROTOCOL_EXTRACTION === "true";

// Blank eCRF → forms/fields/branching extraction (SourceX roadmap Phase 1b).
// Gates the eCRF upload/review route only. Nothing else in SourceX — and
// nothing outside it — changes when this is off.
export const ECRF_EXTRACTION_ENABLED =
  process.env.REACT_APP_ENABLE_ECRF_EXTRACTION === "true";

// Offer "queue it" as an option when extracting a blank eCRF: the read is
// submitted to the asynchronous batch reader (~half price) and collected later
// instead of running while the coordinator waits.
//
// Why it's a flag, and why it is OFF by default: the Anthropic Batch API sits
// outside that BAA, so it may only ever carry the blank eCRF — a sponsor form
// with no patient data. The SERVER has its own independent gate
// (ECRF_BATCH_ENABLED); this one only decides whether the option is offered, so
// turning it on here without the server does nothing but hide the choice again.
export const ECRF_BATCH_ENABLED =
  process.env.REACT_APP_ENABLE_ECRF_BATCH === "true";

// "Add Inclusion/Exclusion" in the template editor: read a study protocol's
// eligibility criteria and build the printed INCLUSION/EXCLUSION CRITERIA
// checklist from them.
//
// Why it's a flag: the button is the only way to reach the
// `extractEligibilityCriteria` Cloud Function, so this and that function have to
// ship together. Without the gate, a client build carrying the button but
// pointed at a project where the function isn't deployed shows a coordinator a
// button that spends a minute and then falls back to a blank checklist — a
// feature that looks broken rather than one that isn't there yet.
//
// OFF is exactly today's behaviour: no button, and nothing else in the editor,
// the exporters or the patient's fill-in changes. A checklist ALREADY in a
// template still renders, prints and exports with this off — the flag gates
// adding one, not having one, so turning it back off can never blank a section
// somebody has already approved.
export const ELIGIBILITY_EXTRACTION_ENABLED =
  process.env.REACT_APP_ENABLE_ELIGIBILITY_EXTRACTION === "true";

// SoA × eCRF join review (SourceX roadmap Phase 2). Gates the route where a
// coordinator confirms or corrects the computed mapping table. Needs BOTH a
// protocol SoA extraction and an eCRF extraction to exist for the study, so it
// is flagged separately from the two ingestion phases that produce them.
export const JOIN_REVIEW_ENABLED =
  process.env.REACT_APP_ENABLE_JOIN_REVIEW === "true";

// Route Research Calendar reads/writes through the authenticated
// `researchCalendar` Cloud Function instead of calling the Apps Script web app
// straight from the browser.
//
// Why it's a flag: the direct path works today, so this can be turned on, watched,
// and turned back off without a code change. OFF = exactly today's behavior.
//
// The calendar itself does not change either way — same Google calendar, same
// events. What changes is that the request carries a Firebase ID token and is
// checked for site membership server-side, and the Apps Script URL stops being
// shipped in the JS bundle.
//
// Once this is on for every site: remove REACT_APP_CALENDAR_API from .env and
// set REGX_SHARED_SECRET on the Apps Script to close the public endpoint.
export const CALENDAR_PROXY_ENABLED =
  process.env.REACT_APP_ENABLE_CALENDAR_PROXY === "true";

// Study-page screening tools: "Upload Protocol" -> Screening Visit checklist,
// and "Find Potential Patients" -> criteria-derived query over the patient
// list. Gates the BUTTONS on StudyDetailsPage only.
//
// Why it's a flag: "Find Potential Patients" is the only caller of the
// `deriveEligibilityQuery` Cloud Function, so the two ship together — same
// reasoning as ELIGIBILITY_EXTRACTION_ENABLED above. A confirmed Screening
// Visit checklist ALREADY on a study still shows on the patient page with this
// off: the flag gates building one, not having one.
export const STUDY_SCREENING_ENABLED =
  process.env.REACT_APP_ENABLE_STUDY_SCREENING === "true";

// Admin "Upload PFTs" page: send scanned PFT report PDFs from the browser straight
// into the PFT extraction job's own private bucket (NOT the portal's main storage,
// whose rules let any signed-in user read and write). The bucket has its own
// storage rule (storage.pft.rules): admins with a valid 2-step session can only
// CREATE PDFs under uploads/<tenant>/<batch>/, and nothing can be read back from
// the browser. OFF = no button and no route; nothing else changes.
export const PFT_UPLOAD_ENABLED =
  process.env.REACT_APP_ENABLE_PFT_UPLOAD === "true";

// The bucket the page uploads into (differs per project: staging has its own).
export const PFT_UPLOAD_BUCKET = process.env.REACT_APP_PFT_UPLOAD_BUCKET || "";

// Patient page "PFT" section: several dated PFTs per patient, always visible
// whatever the diagnosis, plus a Sex field. Rules in services/pftTests.js.
// OFF = the page is exactly as before: the PFT block inside the Asthma / COPD /
// Bronchiectasis sections, and saves write the flat PFT fields from it. ON =
// those blocks are replaced by one section; existing values show as the first
// test, and saves write the flat fields from the newest test (plus `pftTests`
// only once a record has more than its single old PFT).
export const PFT_HISTORY_ENABLED =
  process.env.REACT_APP_ENABLE_PFT_HISTORY === "true";

// Admin upload page "Extract next batch" (cloud PFT extraction into the portal) and the
// file sections (Not extracted yet / Extracting now / Extracted / Failed). Needs the
// startPftExtraction function and the pft-extract job in that project. OFF = the page
// is exactly as before (upload + "Already uploaded" list).
export const PFT_EXTRACTION_ENABLED =
  process.env.REACT_APP_ENABLE_PFT_EXTRACTION === "true";

// Coordinator "PFT Uploads" page (/coordinator/pft-uploads): patients found in extracted
// PFT reports, checked by a person and then moved into Lung Center Referrals. OFF = no
// route and no button; nothing else changes.
export const PFT_IMPORT_ENABLED =
  process.env.REACT_APP_ENABLE_PFT_IMPORT === "true";

// PFT Uploads: text patients the health survey (Text filtered / Text this patient),
// green/orange cards and the survey boxes. Needs the sendPftSurveySms function.
// OFF = the page is exactly as before. Staging first.
export const PFT_SURVEY_TEXT_ENABLED =
  process.env.REACT_APP_ENABLE_PFT_SURVEY_TEXT === "true";

// TESTING ONLY: show the coordinators' weekly-report reminder EVERY day instead
// of only on Fridays and the month's last working day, so it can be tried on
// staging. Set only in .env.staging; must never be set for production.
export const REPORT_REMINDER_EVERY_DAY =
  process.env.REACT_APP_REPORT_REMINDER_EVERY_DAY === "true";

// Protocol Compare (study page, admins only): upload a sponsor's new protocol
// and see every changed word (redline, found by code) plus an AI summary.
// OFF = no button; nothing else changes. Staging first.
export const PROTOCOL_COMPARE_ENABLED =
  process.env.REACT_APP_ENABLE_PROTOCOL_COMPARE === "true";

// Protocol Compare's "Check vs sponsor" tab (the AI scorecard against a
// sponsor's summary of changes). It was a testing aid: hidden from the page by
// default (2026-10-23). The code, the function and any saved checks are kept;
// setting this to "true" shows the tab again.
export const PROTOCOL_SPONSOR_CHECK_ENABLED =
  process.env.REACT_APP_ENABLE_PROTOCOL_SPONSOR_CHECK === "true";

// SourceX built documents: the guided tour ("Would you like a tour?" the first time,
// then a Tour button in the header; 2026-10-29). Explains features only, changes
// nothing stored. Staging first.
export const SOURCEX_TOUR_ENABLED =
  process.env.REACT_APP_ENABLE_SOURCEX_TOUR === "true";

// UI revamp (branch `ui`, 2026-10-30, staging only): the purple + white theme
// (src/theme-v2.css, every rule scoped under the `regx-v2` class on <html>) and,
// later, the personal /home page. Off = the app looks and routes exactly as before.
export const UI_REVAMP_ENABLED =
  process.env.REACT_APP_ENABLE_UI_REVAMP === "true";

// STAGING ONLY (2026-11-02): staging has no Research Calendar connected (the
// real one holds PHI and staging would write to it), so its calendar views are
// empty. With this on, a site with NO calendar connected shows made-up sample
// visits (src/services/sampleVisits.js) instead, to look at; booking stays off.
// A connected calendar is never replaced. Never set this in `.env` (production).
export const CALENDAR_SAMPLE_VISITS =
  process.env.REACT_APP_CALENDAR_SAMPLE_VISITS === "true";

// PRODUCTION-ONLY switches (2026-10-29): set to "true" in `.env` and to "false"
// in `.env.staging` (a staging build falls back to `.env` for any key it lacks).
// Absent = today's behaviour.
//
// VaultX isn't on production yet: the VaultX tile's page (/ebinder, the old
// prototype) shows "VaultX is coming soon" instead. Nothing stored is touched.
export const VAULTX_COMING_SOON =
  process.env.REACT_APP_VAULTX_COMING_SOON === "true";

// Sign-in through Xiva Login (the hub) isn't set up on production yet (no
// hubSignIn function or secret there): /hub-return goes straight to the normal
// login. Turn off once hubSignIn is deployed to production.
export const HUB_SIGNIN_HIDDEN =
  process.env.REACT_APP_HIDE_HUB_SIGNIN === "true";
